import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { fetchYieldImages, fetchYieldRetailPrices, fetchYieldSheet, yieldFileUrls, yieldWrite } from '../services/api';
import {
  RECEIVERS, addTally, applyUnit, blankForm, buildSaveModel, buildSummary, calculateTotalPrice, fileType, finalizeWarnings, formFromSheet, money, newQuantity,
  num2, orderTotals, quantityValue, removeTally, setLive, summaryTables, yieldTotals,
} from './yieldSheetCalc';

/**
 * The admin portal's Add / Edit Yield Sheet pop-up. Same fields, same checks, same totals: the sums
 * live in yieldSheetCalc.js. While ws8_yield_sheet_live is off the sheet opens and the figures work,
 * and nothing can be kept.
 */

export function YieldFlagNote({ live }) {
  if (live) return null;
  return <div className="ys-note">Read only here. Saving still happens in the admin portal until cutover, so changes made on this page are not kept.</div>;
}

const num = (e) => (e.target.value === '' ? null : parseFloat(e.target.value));

export function GradedTable({ title, rows, total, field, locked, onTally, onRemove, onLive }) {
  const inputs = useRef({});
  const add = (i) => {
    const el = inputs.current[i];
    onTally(field, i, el.valueAsNumber);
    el.value = '';
  };
  return (
    <>
      <h4 className="ys-heading">{title} Graded</h4>
      <div className="port-scroll">
        <table className="trans-table ys-graded">
          <thead><tr><th>Graded</th><th>Tally</th><th>Live</th><th>Total</th><th>Total {title} Yielded</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.gradedId}>
                <td>{r.gradedName}</td>
                <td>
                  <div className="ys-tally-add">
                    <input ref={(el) => { inputs.current[i] = el; }} type="number" className="form-input" readOnly={locked} placeholder="Enter value and press Enter"
                      onKeyDown={(e) => { if (e.key === 'Enter' && !locked) { e.preventDefault(); add(i); } }} />
                    <button type="button" className="btn btn-outline" disabled={locked} aria-label="Add tally" onClick={() => add(i)}><Plus size={14} /></button>
                  </div>
                  <div className="ys-tally-cards">
                    {r.tallyValues.map((v, j) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <span className="ys-tally-card" key={`${v}-${j}`}>{v}<button type="button" aria-label="Remove tally" onClick={() => onRemove(field, i, v)}><X size={12} /></button></span>
                    ))}
                  </div>
                </td>
                <td><input type="number" className="form-input" value={r.live ?? ''} disabled={onLive == null} onChange={(e) => onLive(field, i, num(e))} /></td>
                <td>{r.totalGraded}</td>
                {i === 0 && <td rowSpan={rows.length} className="ys-big">{num2(total)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// Reads the two ways Outlook hands a dragged attachment to a browser, as the admin sheet does.
function filesFromDrop(dt) {
  if (!dt) return Promise.resolve([]);
  if (dt.files && dt.files.length) return Promise.resolve(Array.from(dt.files));
  const plain = [];
  const entries = [];
  Array.from(dt.items || []).forEach((it) => {
    if (!it || it.kind !== 'file') return;
    const f = it.getAsFile ? it.getAsFile() : null;
    if (f) { plain.push(f); return; }
    const entry = it.webkitGetAsEntry ? it.webkitGetAsEntry() : null;
    if (entry && entry.isFile) entries.push(entry);
  });
  if (plain.length) return Promise.resolve(plain);
  return Promise.all(entries.map((en) => new Promise((resolve) => {
    try { en.file((f) => resolve(f), () => resolve(null)); } catch (e) { resolve(null); }
  }))).then((list) => list.filter(Boolean));
}

const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.onerror = () => reject(new Error('Could not read that file.'));
  r.readAsDataURL(file);
});

function FileList({ title, images, urls, live, uploading, onFiles, onDelete, say }) {
  const [over, setOver] = useState(false);
  const picker = useRef(null);
  const drop = async (e) => {
    e.preventDefault();
    setOver(false);
    if (!live) return;
    const files = await filesFromDrop(e.dataTransfer);
    if (!files.length) { say('Nothing came through. Windows did not hand that over as a file. In Outlook, right-click the attachment and choose Copy, then click on this page and press Ctrl+V.'); return; }
    onFiles(files);
  };
  const name = (url) => String(url).split('/').pop();
  return (
    <div className="ys-files">
      <span className="ys-label">{title}:</span>
      <div className={`ys-drop${over ? ' on' : ''}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}>
        <button type="button" className="btn btn-outline" disabled={!live} aria-label="Pick a file" onClick={() => picker.current.click()}><Plus size={14} /></button>
        <span>{uploading ? 'Uploading...' : 'Drag a PDF or a photo in here, or use the + to pick one.'}</span>
        <input ref={picker} type="file" multiple hidden onChange={(e) => { onFiles(Array.from(e.target.files)); e.target.value = ''; }} />
      </div>
      {images.filter((x) => ['image', 'pdf'].includes(fileType(x.imageUrl))).map((x) => (
        <div className="ys-file" key={`${x.dataId}-${x.imageId}-${x.imageUrl}`}>
          {fileType(x.imageUrl) === 'image'
            ? <img className="ys-thumb" src={urls[name(x.imageUrl)] || undefined} alt="Invoice" />
            : <a href={urls[name(x.imageUrl)] || undefined} target="_blank" rel="noreferrer">Open PDF</a>}
          <button type="button" className="btn btn-outline" disabled={!live} aria-label="Delete file" onClick={() => onDelete(x)}><Trash2 size={14} /></button>
          <div className="ys-file-url">
            <input type="text" className="form-input" value={x.imageUrl} readOnly />
            <button type="button" className="btn btn-outline" onClick={() => navigator.clipboard && navigator.clipboard.writeText(x.imageUrl)}>Copy URL to clipboard</button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function YieldSheetEditor({ id, lookups, live, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(0);
  const [readonlyMode, setReadonlyMode] = useState(false);
  const [saveAfterFinal, setSaveAfterFinal] = useState(false);
  const [igrLinked, setIgrLinked] = useState(false);
  const [invoiceImages, setInvoiceImages] = useState([]);
  const [transportImages, setTransportImages] = useState([]);
  const [urls, setUrls] = useState({});
  const [uploading, setUploading] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const autosave = useRef(false);
  const lastIndex = useRef(-1);

  const say = (text, good) => setMessage({ text, good: !!good });

  const load = useCallback(async () => {
    autosave.current = false;
    if (id > 0) {
      const [data, images] = await Promise.all([fetchYieldSheet(id), fetchYieldImages(id)]);
      const s = formFromSheet(data);
      setForm(s.form);
      setSelectedUnit(s.selectedUnit);
      setReadonlyMode(s.readonlyMode);
      setSaveAfterFinal(s.saveAfterFinal);
      setIgrLinked(s.igrLinked);
      setInvoiceImages(images.invoice);
      setTransportImages(images.transport);
      lastIndex.current = images.transport.length - 1;
      autosave.current = true;
    } else {
      setForm(blankForm(lookups));
    }
  }, [id, lookups]);

  useEffect(() => { load().catch((e) => say(e.message)); }, [load]);

  useEffect(() => {
    const names = [...invoiceImages, ...transportImages].map((x) => String(x.imageUrl).split('/').pop()).filter((n) => !urls[n]);
    if (names.length) yieldFileUrls(names).then((u) => setUrls((old) => ({ ...old, ...u }))).catch(() => {});
  }, [invoiceImages, transportImages, urls]);

  const totals = useMemo(() => (form ? orderTotals(form.quantities, selectedUnit) : null), [form, selectedUnit]);
  const yields = useMemo(() => (form ? yieldTotals(form.maleGradedRows, form.femaleGradedRows, totals.totalBoxOrBushel) : null), [form, totals]);

  if (!form) {
    return (
      <div className="modal-overlay"><div className="modal ys-modal"><div className="ys-status">{message ? message.text : 'Loading...'}</div>
        <div className="modal-footer"><button type="button" className="btn btn-outline" onClick={onClose}>Close</button></div></div></div>
    );
  }

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setQty = (i, k, v) => set('quantities', form.quantities.map((q, j) => (j === i ? { ...q, [k]: v } : q)));

  // validateForm(false)
  const valid = () => {
    if (form.suplier === '' || form.suplier === 0) { say('Please fill in the Supplier field'); return false; }
    if (form.dateRecevied === '' || form.dateRecevied === 0) { say('Please fill in the Date Received field'); return false; }
    if (form.recevierId === '' || form.recevierId === 0) { say('Please fill in the Receiver ID field'); return false; }
    if (form.unitOfMeasure === '' || form.unitOfMeasure === 0) { say('Please fill in the Unit of Measure field '); return false; }
    const ok = form.quantities.length > 0 && form.quantities.map((q) => quantityValue(q, readonlyMode)).every((item) => {
      // eslint-disable-next-line eqeqeq
      if (selectedUnit == 1 && (item.boxQty === '' || item.boxQty == 0)) return false;
      // eslint-disable-next-line eqeqeq
      if (selectedUnit == 2 && (item.bushelQty === '' || item.bushelQty == 0)) return false;
      return !(item.price === '' || item.price === null || item.price < 0);
    });
    if (!ok) { say('Please add at least 1 item and add all required data'); return false; }
    return true;
  };

  const sendReport = async (purchaseOrderId, model) => {
    const supplier = lookups.suppliers.find((x) => x.value === String(model.PurchaseOrder.crabSupplierId));
    const vendor = supplier ? supplier.label : 'unknown Supplier';
    const s = buildSummary(await fetchYieldSheet(purchaseOrderId), lookups.graded);
    const r = await yieldWrite('yield_report_email', { vendor, dateReceived: form.dateRecevied, subject: model.Subject, body: model.Body, tables: summaryTables(s, vendor), note: s.note });
    if (r.ok) say('Email sent successfully', true);
  };

  // insertUpdatePurchaseOrdersubmit and addPurchaseOrder.
  const submit = async (wanted, wantSilent, subject = null, body = null, current = form) => {
    let status = wanted;
    let silent = wantSilent;
    if (saveAfterFinal && status === 2) { status = 1; silent = false; }
    if (!live || !(autosave.current || !silent)) return;
    const model = buildSaveModel({ form: current, purchaseOrderId: id, status, selectedUnit, readonlyMode, invoiceImages, transportImages, subject, body });
    if (!silent) setBusy(true);
    try {
      const out = await yieldWrite('yield_save', { model });
      if (silent) return;
      setBusy(false);
      const [savedId, warned] = out.response;
      if (savedId > 0) {
        say(id > 0 ? 'Updated successfully' : 'Saved successfully', true);
        if (status === 1 && !saveAfterFinal) await sendReport(savedId, model);
      } else {
        say('Error');
      }
      if (warned === 1) say('This Yield Sheet has low ROI, please bring this to Matt\u2019s attention.');
      setTimeout(() => onSaved(), 3000);
    } catch (e) {
      if (!silent) { setBusy(false); say(e.message); }
    }
  };

  const changeGrade = (field, rows) => {
    const next = { ...form, [field]: rows };
    setForm(next);
    if (id > 0) submit(2, true, null, null, next);
  };

  const finalize = async () => {
    if (!valid()) return;
    if (!window.confirm('Are you sure, you want to finalize this Yield Sheet? Once finalized, you will only be able to change invoicing!')) return;
    const prices = await fetchYieldRetailPrices().catch(() => []);
    const { subject, body } = finalizeWarnings({ form, totals, yields, prices });
    submit(1, false, subject, body);
  };

  const unfinalize = async () => {
    if (!window.confirm('Are you sure, you want to unfinalize this Yield Sheet?')) return;
    setBusy(true);
    try {
      const r = await yieldWrite('yield_unfinalize', { purchaseOrderId: id });
      await load();
      say(r.ok ? 'Updated Successfully' : 'The status changed, but the email did not go.', r.ok);
    } catch (e) { say(e.message); }
    setBusy(false);
  };

  // confirmUnitOfMeasureChange. Refusing leaves the unit as it was, so the select goes back.
  const changeUnit = (value) => {
    // eslint-disable-next-line eqeqeq
    if (selectedUnit != 0 && form.quantities.length !== 0) {
      // eslint-disable-next-line eqeqeq
      const ask = value == 1 ? 'Do you want to Change? On change the value of Bushels will be zero' : 'Do you want to Change? On change the value of Boxes will be zero';
      if (!window.confirm(ask)) return;
    }
    setForm({ ...form, unitOfMeasure: value, quantities: applyUnit(form.quantities, value) });
    setSelectedUnit(value);
  };

  const checkDescription = (i, value) => {
    const taken = form.quantities.some((q, j) => j !== i && parseInt(q.description, 10) === parseInt(value, 10));
    if (taken) { say('Please select another option; this one has already been chosen.'); setQty(i, 'description', ''); return; }
    setQty(i, 'description', value);
  };

  const removeRow = async (i) => {
    if (form.quantities.length <= 1) { say('unable to remove last item'); return; }
    const itemId = form.quantities[i].purchaseOrderItemId;
    if (itemId > 0) {
      if (!window.confirm('Do you want to delete?')) return;
      // The admin sheet keeps the row on screen after this and drops it on the next open.
      try { await yieldWrite('yield_delete_item', { purchaseOrderItemId: itemId }); } catch (e) { say(e.message); }
      return;
    }
    set('quantities', form.quantities.filter((_, j) => j !== i));
  };

  const attach = (zone) => async (files) => {
    setUploading(zone);
    let added = 0;
    const rows = [];
    for (const file of files) {
      try {
        const r = await yieldWrite('yield_file_upload', { fileName: file.name, base64: await toBase64(file) });
        lastIndex.current += 1;
        rows.push({ imageId: lastIndex.current, dataId: null, imageUrl: `Files/Images/PurchaseOrder/${r.name.toLowerCase().endsWith('.pdf') ? 'Large/' : ''}${r.name}` });
        added += 1;
      } catch (e) { say(`${file.name} could not be uploaded: ${e.message}`); }
    }
    if (zone === 'transport') setTransportImages((x) => [...x, ...rows]); else setInvoiceImages((x) => [...x, ...rows]);
    setUploading(null);
    if (added) say(`${added === 1 ? 'Attached' : `${added} files attached`}. Save the yield sheet to keep ${added === 1 ? 'it' : 'them'}.`, true);
  };

  const removeFile = (imageType) => async (image) => {
    if (!window.confirm('Do you want to delete?')) return;
    try {
      const r = await yieldWrite('yield_delete_image', { imageUrl: image.imageUrl, dataId: image.dataId, imageType });
      if (!r.ok) { say('Error while deleting image'); return; }
      const keep = (list) => list.filter((x) => x.imageUrl !== image.imageUrl);
      if (imageType === 1) setInvoiceImages(keep); else setTransportImages(keep);
      say('Image deleted successfully', true);
    } catch (e) { say(e.message); }
  };

  const close = () => { if (window.confirm('Do you want to close this modal?')) onClose(); };
  const lockedTally = readonlyMode || igrLinked;
  const canSave = live && !busy;
  return (
    <div className="modal-overlay">
      <div className="modal ys-modal">
        <div className="modal-header"><h2>{id > 0 ? 'Edit Yield Sheet' : 'Add Yield Sheet'}</h2><button type="button" className="modal-close" aria-label="Close" onClick={close}>&times;</button></div>
        <div className="modal-body">
          <YieldFlagNote live={live} />
          {message && <div className={`ys-note ${message.good ? 'ys-note-good' : 'ys-note-bad'}`}>{message.text}</div>}
          <div className="ys-title-row">
            <h4 className="ys-heading">Purchased From</h4>
          </div>
          <div className="ys-grid">
            <label className="ys-field">Supplier:
              <select className="form-input" value={form.suplier} disabled={readonlyMode} onChange={(e) => set('suplier', e.target.value)}>
                <option value="" disabled>Select Supplier</option>
                {lookups.suppliers.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
            <label className="ys-field">Date Received:<input type="date" className="form-input" value={form.dateRecevied || ''} disabled={readonlyMode} onChange={(e) => set('dateRecevied', e.target.value)} /></label>
            <label className="ys-field">Receiver:
              <select className="form-input" value={form.recevierId} disabled={readonlyMode} onChange={(e) => set('recevierId', e.target.value)}>
                <option value="" disabled>Select Receiver</option>
                {RECEIVERS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
            <label className="ys-field">Unit of Measure:
              <select className="form-input" value={form.unitOfMeasure} disabled={readonlyMode} onChange={(e) => changeUnit(e.target.value)}>
                <option value="" disabled>Select Unit of Measure</option>
                {lookups.units.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </label>
            <label className="ys-field">Date Processed:<input type="date" className="form-input" value={form.dateProcessed || ''} disabled={readonlyMode} onChange={(e) => set('dateProcessed', e.target.value)} /></label>
          </div>

          <div className="ys-title-row">
            <h4 className="ys-heading">Purchased Items:</h4>
            <button type="button" className="btn btn-outline" disabled={readonlyMode} onClick={() => set('quantities', [...form.quantities, newQuantity(selectedUnit)])}>Add item</button>
          </div>
          <div className="port-scroll">
            <table className="trans-table ys-items">
              <thead><tr><th>Box Quantity</th><th>Weight</th><th>Bushel Quantity</th><th>Transportation Cost Per Item</th><th>Description</th><th>Price ($)</th><th>Total Price ($)</th><th>Actions</th></tr></thead>
              <tbody>
                {form.quantities.map((q, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={i}>
                    <td><input type="number" className="form-input" value={q.boxQty ?? ''} disabled={readonlyMode || q.boxOff} onChange={(e) => setQty(i, 'boxQty', num(e))} /></td>
                    <td><input type="number" className="form-input" value={q.weight ?? ''} disabled={readonlyMode} onChange={(e) => setQty(i, 'weight', num(e))} /></td>
                    <td><input type="number" className="form-input" value={q.bushelQty ?? ''} disabled={readonlyMode || q.bushelOff} onChange={(e) => setQty(i, 'bushelQty', num(e))} /></td>
                    <td><input type="number" className="form-input" value={q.itemTransportationCost ?? ''} disabled={readonlyMode} onChange={(e) => setQty(i, 'itemTransportationCost', num(e))} /></td>
                    <td>
                      <select className="form-input" value={q.description} disabled={readonlyMode} onChange={(e) => checkDescription(i, e.target.value)}>
                        <option value="" disabled>Select Description</option>
                        {lookups.descriptions.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </select>
                    </td>
                    <td><input type="number" className="form-input" value={q.price ?? ''} disabled={readonlyMode} onChange={(e) => setQty(i, 'price', num(e))} /></td>
                    <td><input type="text" className="form-input" readOnly value={money(calculateTotalPrice(quantityValue(q, readonlyMode)))} /></td>
                    <td><button type="button" className="btn btn-outline" disabled={readonlyMode || (!live && q.purchaseOrderItemId > 0)} onClick={() => removeRow(i)}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="ys-grid">
            <label className="ys-field">Total Amount ($):<input type="text" className="form-input" readOnly value={money(totals.totalAmount)} /></label>
            {/* eslint-disable-next-line eqeqeq */}
            <label className="ys-field">Total Crabs:<input type="text" className="form-input" readOnly value={`${totals.totalCrab}${selectedUnit == 1 ? ' LBS' : ' BU'}`} /></label>
          </div>

          <h4 className="ys-heading">Invoice</h4>
          <div className="ys-grid">
            <label className="ys-field">Invoice Number:<input type="text" className="form-input" value={form.invoiceNumber || ''} onChange={(e) => set('invoiceNumber', e.target.value)} /></label>
            <label className="ys-field">Invoice Date:<input type="date" className="form-input" value={form.invoiceDate || ''} onChange={(e) => set('invoiceDate', e.target.value)} /></label>
            <label className="ys-field">Invoice Amount ($):<input type="text" className="form-input" value={form.invoiceAmount} disabled={readonlyMode} onChange={(e) => set('invoiceAmount', e.target.value)} /></label>
          </div>
          <FileList title="Invoice Images" images={invoiceImages} urls={urls} live={live} uploading={uploading === 'invoice'} onFiles={attach('invoice')} onDelete={removeFile(1)} say={say} />

          <h4 className="ys-heading">Transportation Details</h4>
          <div className="ys-grid">
            <label className="ys-field">Transportation Invoice Date:<input type="date" className="form-input" value={form.transparentInvoiceDate || ''} onChange={(e) => set('transparentInvoiceDate', e.target.value)} /></label>
            <label className="ys-field">Transportation Cost ($):<input type="text" className="form-input" readOnly value={money(totals.transportationAmount)} /></label>
          </div>
          <FileList title="Transportation Invoice Images" images={transportImages} urls={urls} live={live} uploading={uploading === 'transport'} onFiles={attach('transport')} onDelete={removeFile(2)} say={say} />

          <h4 className="ys-heading">Summary</h4>
          <div className="port-scroll">
            <table className="trans-table ys-graded">
              <thead><tr><th>BU/BX/CR to sort</th><th>Total</th><th>Dead</th><th>Hold for live</th><th>Total % yielded</th></tr></thead>
              <tbody>
                {form.yieldSummary.map((r, i) => {
                  const setRow = (k) => (e) => set('yieldSummary', form.yieldSummary.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
                  return (
                    <tr key={r.typeId}>
                      <td>{r.typeName}</td>
                      <td><input type="text" className="form-input" value={r.total ?? ''} disabled={readonlyMode} onChange={setRow('total')} /></td>
                      <td><input type="text" className="form-input" value={r.dead ?? ''} disabled={readonlyMode} onChange={setRow('dead')} /></td>
                      <td><input type="text" className="form-input" value={r.holdForLive ?? ''} disabled={readonlyMode} onChange={setRow('holdForLive')} /></td>
                      {i === 0 && <td rowSpan={form.yieldSummary.length} className="ys-big">{`${yields.totalYieldedPercentage} %`}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <h4 className="ys-heading">Yield</h4>
          <div className="ys-grid"><label className="ys-field">Date Cooked:<input type="date" className="form-input" value={form.dateCooked || ''} disabled={readonlyMode} onChange={(e) => set('dateCooked', e.target.value)} /></label></div>
          <GradedTable title="Male" rows={form.maleGradedRows} total={yields.totalMaleYielded} field="maleGradedRows" locked={lockedTally}
            onTally={(f, i, v) => changeGrade(f, addTally(form[f], i, v))} onRemove={(f, i, v) => changeGrade(f, removeTally(form[f], i, v))} onLive={readonlyMode ? null : (f, i, v) => changeGrade(f, setLive(form[f], i, v))} />
          <GradedTable title="Female" rows={form.femaleGradedRows} total={yields.totalFemaleYielded} field="femaleGradedRows" locked={lockedTally}
            onTally={(f, i, v) => changeGrade(f, addTally(form[f], i, v))} onRemove={(f, i, v) => changeGrade(f, removeTally(form[f], i, v))} onLive={readonlyMode ? null : (f, i, v) => changeGrade(f, setLive(form[f], i, v))} />

          <div className="ys-grid">
            <label className="ys-field ys-wide">Notes:<textarea className="form-input" rows={3} value={form.notes || ''} disabled={readonlyMode} onChange={(e) => set('notes', e.target.value)} /></label>
            <label className="ys-field">Dead:<input type="number" className="form-input" value={form.dead ?? ''} disabled={readonlyMode} onChange={(e) => set('dead', num(e))} /></label>
          </div>
        </div>
        <div className="modal-footer ys-footer">
          <button type="button" className="btn btn-outline" onClick={close}>Cancel</button>
          <button type="button" className="btn btn-outline" disabled={!canSave || readonlyMode} onClick={finalize}>Finalize</button>
          <button type="button" className="btn btn-outline" disabled={!canSave || !readonlyMode} onClick={unfinalize}>Unfinalize</button>
          <button type="button" className="btn btn-primary" disabled={!canSave} onClick={() => submit(2, false)}>Save</button>
        </div>
      </div>
    </div>
  );
}